import {
  getHardwareMonitoringStatus,
  GetHardwareMonitoringStatusBody,
} from "@intelligentlilli/api-layer";
import { useCallback, useEffect, useState } from "react";
import { determineHardwareStatus } from "../Utils/newDeviceMonitoring/determineHardwareStatus";
import { useAppSelector } from "State/hooks";

/**
 * Custom hook to fetch the hardware monitoring status of a specific device.
 *

 * const { loading, error, data } = useGetHardwareStatus({ isInstalled: true, serviceUserId: "user456", devicestring: "device123" });
 */
export const useGetHardwareStatus = ({
  serviceUserId,
  isInstalled,
  devicestring,
}: {
  serviceUserId: string;
  isInstalled?: boolean;
  devicestring?: string;
}) => {
  // redux state
  const server = useAppSelector((state) => state.session.server);

  // local state
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<GetHardwareMonitoringStatusBody | null>(
    null
  );

  const getHardwareStatus = useCallback(() => {
    if (!isInstalled || !devicestring) return;
    setLoading(true);
    setError(false);
    getHardwareMonitoringStatus(server, serviceUserId, devicestring, "web")
      .then((res) => {
        if (res.ok && res.body) {
          setLoading(false);
          // Filter out removed sensors, apart from the hub, which does not have a location property
          const currentSensors = res.body.filter(sensor => !sensor?.location?.includes('-REMOVED') || sensor?.child_device_internal_id === "hub");
          setData(currentSensors);
        } else {
          setLoading(false);
          setError(true);
          setData(null);
        }
      })
      .catch(() => {
        setLoading(false);
        setError(true);
        setData(null);
      });
  }, [devicestring, isInstalled, server, serviceUserId]);

  useEffect(() => {
    getHardwareStatus();
  }, [getHardwareStatus]);

  return {
    loading,
    error,
    data,
    getHardwareStatus,
    sensorStatus: determineHardwareStatus(data, !!isInstalled),
  };
};
